import { useState } from "react";
import Welcome from "./steps/welcome";
import AccountCreation from "./steps/account-creation";
import BusinessDetails from "./steps/business-details";
import FinancialSetup from "./steps/financial-setup";
import ConnectAccounts from "./steps/connect-accounts";
import SelectPlan from "./steps/select-plan";
import Confirmation from "./steps/confirmation";

export interface OnboardingData {
  // Account Information
  firstName: string;
  lastName: string;
  fullName: string;
  email: string;
  password: string;
  username?: string;
  emailNotifications: {
    reportsSummary: boolean;
    taskReminders: boolean;
    subscriptionAlerts: boolean;
    marketingEmails: boolean;
  };
  
  // Business Information
  businessName?: string;
  businessType?: string;
  industry?: string;
  ein?: string;
  address?: {
    street?: string;
    city?: string;
    state?: string;
    zip?: string;
    country?: string;
  };
  phoneNumber?: string;
  website?: string;
  
  // Financial Setup
  fiscalYearEnd?: Date | null;
  taxFilingFrequency?: string;
  accountingMethod?: string;
  baseCurrency?: string;
  chartOfAccountsTemplate?: string;
  
  // Bank Connections
  bankConnected?: boolean;
  bankAccounts?: string[];
  connectedAccounts?: Array<{
    id: string;
    name: string;
    type: string;
    balance: string;
  }>;
  
  // Subscription
  selectedPlan?: number; // 1, 2, or 3 for tier level
  trialEndDate?: Date | null;
  billingCycle?: string;
  paymentMethod?: string;
  subscriptionPlanId?: string;
  
  // Onboarding Meta
  startedAt?: Date;
  completedAt?: Date | null;
  currentStep: number;
}

const defaultOnboardingData: OnboardingData = {
  firstName: "",
  lastName: "",
  fullName: "",
  email: "",
  password: "",
  emailNotifications: {
    reportsSummary: true,
    taskReminders: true,
    subscriptionAlerts: true,
    marketingEmails: false,
  },
  currentStep: 0,
};

export default function Onboarding() {
  const [onboardingData, setOnboardingData] = useState<OnboardingData>(defaultOnboardingData);
  const [currentStep, setCurrentStep] = useState(0);
  
  // Define step names for progress tracking
  const stepNames = [
    "Welcome", 
    "Account Creation", 
    "Business Details", 
    "Financial Setup", 
    "Connect Accounts", 
    "Select Plan", 
    "Confirmation"
  ];
  
  // Function to update onboarding data
  const updateOnboardingData = (data: Partial<OnboardingData>) => {
    setOnboardingData(prev => ({
      ...prev,
      ...data,
    }));
  };
  
  // Navigation functions
  const goToNextStep = () => {
    setCurrentStep(prev => {
      const nextStep = prev + 1;
      // Also update currentStep in the onboarding data
      updateOnboardingData({ currentStep: nextStep });
      return nextStep;
    });
  };
  
  const goToPreviousStep = () => {
    setCurrentStep(prev => {
      const prevStep = Math.max(0, prev - 1);
      // Also update currentStep in the onboarding data
      updateOnboardingData({ currentStep: prevStep });
      return prevStep;
    });
  };
  
  // Render appropriate step based on current step value
  const renderStep = () => {
    switch (currentStep) {
      case 0:
        return (
          <Welcome 
            onNext={goToNextStep}
            onboardingData={onboardingData}
            updateOnboardingData={updateOnboardingData}
          />
        );
      case 1:
        return (
          <AccountCreation 
            onNext={goToNextStep}
            onPrevious={goToPreviousStep}
            onboardingData={onboardingData}
            updateOnboardingData={updateOnboardingData}
          />
        );
      case 2:
        return (
          <BusinessDetails 
            onNext={goToNextStep}
            onPrevious={goToPreviousStep}
            onboardingData={onboardingData}
            updateOnboardingData={updateOnboardingData}
          />
        );
      case 3:
        return (
          <FinancialSetup 
            onNext={goToNextStep}
            onPrevious={goToPreviousStep}
            onboardingData={onboardingData}
            updateOnboardingData={updateOnboardingData}
          />
        );
      case 4:
        return (
          <ConnectAccounts 
            onNext={goToNextStep}
            onPrevious={goToPreviousStep}
            onboardingData={onboardingData}
            updateOnboardingData={updateOnboardingData}
          />
        );
      case 5:
        return (
          <SelectPlan 
            onNext={goToNextStep}
            onPrevious={goToPreviousStep}
            onboardingData={onboardingData}
            updateOnboardingData={updateOnboardingData}
          />
        );
      case 6:
        return (
          <Confirmation 
            onNext={goToNextStep}
            onPrevious={goToPreviousStep}
            onboardingData={onboardingData}
            updateOnboardingData={updateOnboardingData}
          />
        );
      default:
        return (
          <div className="flex flex-col items-center justify-center h-screen">
            <h2>Loading...</h2>
          </div>
        );
    }
  };
  
  // Render progress indicator
  const renderProgressIndicator = () => {
    return (
      <div className="max-w-3xl mx-auto mb-8">
        <div className="flex items-center justify-between">
          <div className="flex-1 h-2 bg-neutral-200 rounded-full overflow-hidden">
            <div 
              className="h-full bg-primary rounded-full transition-all duration-300"
              style={{ width: `${(currentStep / (stepNames.length - 1)) * 100}%` }}
            />
          </div>
          <div className="ml-4 text-sm text-neutral-500">
            Step {currentStep + 1} of {stepNames.length}
          </div>
        </div>
        <div className="mt-2 text-sm font-medium">
          {stepNames[currentStep]}
        </div>
      </div>
    );
  };
  
  return (
    <div className="min-h-screen bg-neutral-50 py-8 px-4">
      {currentStep > 0 && renderProgressIndicator()}
      {renderStep()}
    </div>
  );
}